import React, { useCallback } from 'react';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { AdminFormSection } from '@ui';
import { OpoContactDetailsSection } from '@lp/features/admin/forms';
import { CreateOpoContactFormValues } from './types';
import { OpoOrgMultiSelect } from './components/OpoOrgMultiSelect';
import { OpoMembershipList } from './components/OpoMembershipList';

type Props = {
  isReadOnly?: boolean;
  isPrimary?: boolean;
};

export const OpoContactForm = React.memo(({ isReadOnly = false, isPrimary = false }: Props) => {
  const { control, setValue, getValues } = useFormContext<CreateOpoContactFormValues>();

  const { fields, replace } = useFieldArray({
    control,
    name: 'memberships',
  });

  const inviteAsUser = useWatch({ control, name: 'inviteAsUser' });
  const isOpoUser = useWatch({ control, name: 'isOpoUser' });

  const shouldRequireOpoUserFields = inviteAsUser || isOpoUser;

  const handleInviteAsUserChange = useCallback(
    (value: boolean) => {
      setValue('inviteAsUser', value, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (value || isOpoUser) return;

      const memberships = getValues('memberships') ?? [];

      setValue(
        'memberships',
        memberships.map((membership) => ({
          ...membership,
          roles: [],
        })),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    },
    [getValues, isOpoUser, setValue],
  );

  return (
    <>
      <OpoContactDetailsSection
        isReadOnly={isReadOnly}
        isPrimary={isPrimary}
        onInviteAsUserChange={handleInviteAsUserChange}
      />

      <AdminFormSection title="OPO Organization(s)" isReadOnly={isReadOnly}>
        <OpoOrgMultiSelect isReadOnly={isReadOnly} replace={replace} isRequired={shouldRequireOpoUserFields} />

        {shouldRequireOpoUserFields && <OpoMembershipList fields={fields} isReadOnly={isReadOnly} isRequired />}
      </AdminFormSection>
    </>
  );
});

OpoContactForm.displayName = 'OpoContactForm';
